import { HttpParams } from "@angular/common/http";
import { Component, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit, QueryList } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import * as _ from 'lodash';
import * as moment_tz from 'moment-timezone';
import { Subscription } from "rxjs";
import { ErrorMessages } from "../../common/constants";
import { Alert, Location, LocationSchema, Metric, MetricType, MetricValueType, Thing, ThingAlertStatus, Value } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { ContextService } from "../../service/context.service";
import { CountryService } from "../../service/country.service";
import { CustomLabels, CustomLabelService } from "../../service/custom-label.service";
import { CustomPropertyService } from "../../service/custom-property.service";
import { DataService } from "../../service/data.service";
import { CUSTOM_RANGE, DateRangeName, DateRangeService, PeriodVariable } from "../../service/date-range.service";
import { FieldService } from "../../service/field.service";
import { FilterService } from "../../service/filter.service";
import { HttpService } from "../../service/http.service";
import { ProductModelService } from "../../service/product-model.service";
import { UserEventService } from "../../service/user-event.service";
import { UserThingService } from "../../service/user-thing.service";
import { AdvancedSearchService } from "../../shared/advanced-search/advanced-search.service";
import { ThingAdvancedSearchComponent } from "../../shared/advanced-search/thing-advanced-search/thing-advanced-search.component";
import { AbstractContextService } from "../../shared/class/abstract-context-service.class";
import { MetricAggregationType, MetricDetailComponent, PropertyComponent } from "../../shared/component";
import { LocationSchemaThingGroupComponent } from "../../shared/component/location-schema-thing-group/location-schema-thing-group.component";
import { DatetimeFormatterPipe, LocalizationPipe } from "../../shared/pipe";
import { UnderscoreRemoverPipe } from "../../shared/pipe/underscore-remover.pipe";
import { ErrorUtility } from "../../utility/error-utility";
import { LayoutType } from "../shared/details-widget";
import { ThingListWidgetV2Service } from "../thing-list/thing-list-widget-v2.service";
import { LocationSchemaPlanViewDisplayedValue } from "./location-schema-plan-viewer-details.component";
import { LocationSchemaService } from "./location-schema-widget.service";

@Component({
    selector: 'location-schema-plan-viewer',
    template: require('./location-schema-plan-viewer.component.html'),
    styles: [require('./location-schema-plan-viewer.component.css')],
    providers: [AdvancedSearchService, ThingListWidgetV2Service]
})
export class LocationSchemaPlanViewerComponent implements OnInit, OnDestroy {

    @Input() locationSchema: LocationSchema;

    @Input() thingGroups: LocationSchemaThingGroupComponent[];

    @Input() metrics: Metric[];

    @Input() defaultPeriod: DateRangeName;

    @Input() periodRef: string;

    @Input() detailsLayout: LayoutType;

    linkedThingIds: string[];
    filteredLocationSchema: LocationSchema;
    thingIdEventsMap: { [id: string]: Alert } = {};
    loaded: boolean;
    error: string;
    planViewData: LocationSchemaPlanViewData;
    displayedValueOptions: { label: string, value: string }[] = [
        { label: 'Health Status', value: LocationSchemaPlanViewerComponent.HEALTH_STATUS }
    ];
    selectedDisplayedValue: string = LocationSchemaPlanViewerComponent.HEALTH_STATUS;
    visualizedDataDisclaimer: string;
    range: DateRange<moment.Moment>;

    private metricComponentMap: { [metricName: string]: MetricDetailComponent } = {};
    private thingIdColumnComponentMap: { [id: string]: QueryList<PropertyComponent | MetricDetailComponent> };
    private thingMap: { [id: string]: Thing };
    private locationSchemaThingIds: string[];
    private selectedDateRangeName: string;
    private customLabels: CustomLabels = {};
    private defaultNullValue: string;
    private periodFieldSubscription: Subscription;
    private periodFieldRefTimeoutId: any;
    private timezone: string;
    private periodSubscriptionInitCompleted: boolean;
    private refreshIntervalMillis = 30000;
    private intervalId: any;

    static HEALTH_STATUS = "HEALTH_STATUS";

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => FieldService)) private fieldService: FieldService,
        @Inject(forwardRef(() => AdvancedSearchService)) private advancedSearchService: AdvancedSearchService,
        @Inject(forwardRef(() => ProductModelService)) private productModelService: ProductModelService,
        @Inject(forwardRef(() => UserThingService)) private userThingService: UserThingService,
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetV2Service: ThingListWidgetV2Service,
        @Inject(forwardRef(() => LocationSchemaService)) private locationSchemaService: LocationSchemaService,
        @Inject(forwardRef(() => UserEventService)) private userEventService: UserEventService,
        @Inject(forwardRef(() => DataService)) protected dataService: DataService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => UnderscoreRemoverPipe)) private underscoreRemoverPipe: UnderscoreRemoverPipe,
        @Inject(forwardRef(() => CustomLabelService)) private customLabelService: CustomLabelService,
        @Inject(forwardRef(() => ContextService)) private timezoneContextService: ContextService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => FilterService)) private filterService: FilterService,
        @Inject(forwardRef(() => CountryService)) private countryService: CountryService
    ) { }

    ngOnInit(): void {
        this.locationSchemaThingIds = this.locationSchemaService.getThingIdsFromLocationSchema(this.locationSchema);
        this.defaultNullValue = this.authenticationService.getTenant().defaultNullValue || 'N/A';
        this.timezone = this.timezoneContextService.getTimezone();
        if (this.periodRef) {
            this.subscribeToPeriodField();
        } else {
            this.selectedDateRangeName = this.defaultPeriod || DateRangeName.LAST_7_DAYS;
            this.init();
        }
    }

    private updateRange(): void {
        if (this.selectedDateRangeName != CUSTOM_RANGE) {
            if (!this.dateRangeService.getCustomDateRangeByName(this.selectedDateRangeName)) {
                this.selectedDateRangeName = DateRangeName.LAST_7_DAYS;
            }
            const daterange = this.dateRangeService.getCustomDateRangeByName(this.selectedDateRangeName);
            this.range = new DateRange(daterange.range.start, daterange.range.end);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeFromPeriodField();
        this.stopRefreshing();
    }

    private subscribeToPeriodField(): void {
        if (this.periodRef) {
            this.periodFieldRefTimeoutId = setTimeout(() => {
                console.error("Unresolved periodRef");
                this.init();
            }, 5000);
            this.periodFieldSubscription = this.fieldService.subscribeToFields([this.periodRef]).subscribe(fieldsMap => {
                const period: PeriodVariable = fieldsMap ? fieldsMap[this.periodRef] : null;
                if (period != null) {
                    this.clearPeriodFieldRefTimeout();
                    if (!period.start || !period.end) {
                        this.selectedDateRangeName = DateRangeName.LAST_7_DAYS;
                    } else {
                        this.selectedDateRangeName = period.name || CUSTOM_RANGE;
                        if (this.selectedDateRangeName == CUSTOM_RANGE) {
                            this.range = new DateRange(moment_tz.tz(period.start, this.timezone), moment_tz.tz(period.end, this.timezone));
                        }
                    }
                    if (!this.periodSubscriptionInitCompleted) {
                        this.periodSubscriptionInitCompleted = true;
                        this.init();
                    } else {
                        this.loaded = false;
                        this.stopRefreshing();
                        this.updatePlanViewData().then(() => this.startRefreshing());
                    }
                }
            });
        }
    }

    private unsubscribeFromPeriodField(): void {
        if (this.periodRef) {
            this.fieldService.unsubscribeFromFields([this.periodRef]);
            this.periodFieldSubscription.unsubscribe();
        }
        this.clearPeriodFieldRefTimeout();
    }

    private clearPeriodFieldRefTimeout(): void {
        if (this.periodFieldRefTimeoutId) {
            clearTimeout(this.periodFieldRefTimeoutId);
            this.periodFieldRefTimeoutId = null;
        }
    }

    private init(): void {
        this.customLabelService.getCustomLabels().then(customLabels => {
            this.customLabels = customLabels;
            this.initFilteredLocationSchema();
            this.initLinkedThingIds();
            this.handleThingGroups();
            this.initPlanViewData();
        });
    }

    private initFilteredLocationSchema(): void {
        let filteredLocationSchema: LocationSchema = new LocationSchema();
        filteredLocationSchema.id = this.locationSchema.id;
        filteredLocationSchema.locationId = this.locationSchema.locationId;
        filteredLocationSchema.productionLines = [];
        this.locationSchema.productionLines?.forEach(pl => {
            const filteredWorkCells = pl.workCells?.filter(wc => wc.thingIds?.length > 0);
            if (filteredWorkCells?.length > 0) {
                filteredLocationSchema.productionLines.push({ id: pl.id, name: pl.name, workCells: filteredWorkCells });
            }
        });
        this.filteredLocationSchema = filteredLocationSchema;
    }

    private initLinkedThingIds(): void {
        let thingIdOccurenceMap: { [id: string]: number } = {};
        this.locationSchema.productionLines?.forEach(pl => {
            pl.workCells?.forEach(wc => {
                wc.thingIds?.forEach(thingId => {
                    if (thingIdOccurenceMap[thingId]) {
                        thingIdOccurenceMap[thingId]++;
                    } else {
                        thingIdOccurenceMap[thingId] = 1;
                    }
                });
            });
        });
        this.linkedThingIds = Object.entries(thingIdOccurenceMap).filter(entry => entry[1] > 1).map(entry => entry[0]);
    }

    private handleThingGroups(): void {
        this.thingGroups.forEach(group => {
            group.columnComponents.forEach(component => {
                if (component instanceof MetricDetailComponent) {
                    this.metricComponentMap[component.name] = component;
                    const metric = this.metrics.find(m => m.name == component.name);
                    if (!this.displayedValueOptions.some(option => option.value == component.name)) {
                        this.displayedValueOptions.push({ value: component.name, label: component.label || metric?.label || metric?.name });
                    }
                }
            });
        });
    }

    private initPlanViewData(): void {
        let promises = [];
        promises.push(this.getThingIdColumnComponentMap());
        promises.push(this.getThingMap());
        Promise.all(promises).then(results => {
            this.thingIdColumnComponentMap = results[0];
            this.thingMap = results[1];
            this.updatePlanViewData().then(() => this.startRefreshing());
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private updatePlanViewData(): Promise<any> {
        let promises = [];
        this.updateRange();
        promises.push(this.getThingIdEventMap());
        promises.push(this.getThingIdMetricValueMap());
        return Promise.all(promises).then(results => {
            const thingDataMap: { [id: string]: LocationSchemaPlanViewThingData } = this.getThingDataMap(results[0], results[1]);
            const workCellDataMap: { [id: string]: LocationSchemaPlanViewWorkCellData } = this.getWorkCellDataMap(results[0], results[1]);
            this.planViewData = { thingDataMap: thingDataMap, productionLineDataMap: this.getProductionLineDataMap(results[0], workCellDataMap), workCellDataMap: workCellDataMap };
            this.loaded = true;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private getThingMap(): Promise<{ [id: string]: Thing }> {
        let params = new HttpParams();
        this.locationSchemaThingIds.forEach(id => params = params.append('selectedThingId', id));
        return this.userThingService.getRecursivelyAllThings(null, null, this.locationSchema.locationId, null, null, params).then(things => {
            let results: { [id: string]: Thing } = {};
            things.forEach(t => {
                results[t.id] = t;
            });
            return results;
        });
    }

    private getThingIdColumnComponentMap(): Promise<{ [thingId: string]: QueryList<PropertyComponent | MetricDetailComponent> }> {
        let resultMap: { [id: string]: QueryList<PropertyComponent | MetricDetailComponent> } = {};
        const thingAdvancedSearchComponent = new ThingAdvancedSearchComponent(this.httpService, this.authenticationService, this.contextService, this.customPropertyService, this.localizationPipe, this.fieldService, this.advancedSearchService, this.productModelService, this.countryService);
        thingAdvancedSearchComponent.ngOnInit();
        let promises = [];
        this.thingGroups.filter(group => group.query).forEach(group => {
            const body = thingAdvancedSearchComponent.getEncodedQueryFieldBodyFromQuery(group.query);
            const locationSchemaLocation = new Location();
            locationSchemaLocation.id = this.locationSchema.locationId;
            let params = this.thingListWidgetV2Service.getParams(new Set<string>(), null, body, locationSchemaLocation, true, false, null, false, false);
            promises.push(this.userThingService.getRecursivelyAllThings(null, null, null, null, null, params).then(things => {
                things.forEach(t => {
                    if (!resultMap[t.id]) {
                        resultMap[t.id] = group.columnComponents;
                    }
                })
            }));
        });
        const defaultComponents = this.thingGroups.find(group => !group.query && group.columnComponents?.length)?.columnComponents || this.getDefaultColumnComponents();
        return Promise.all(promises).then(() => {
            if (defaultComponents) {
                this.locationSchemaThingIds.forEach(id => {
                    if (!resultMap[id]) {
                        resultMap[id] = defaultComponents;
                    }
                });
            }
            return resultMap;
        });
    }

    private getDefaultColumnComponents(): QueryList<PropertyComponent> {
        const component = new PropertyComponent();
        component.name = 'name';
        const queryList = new QueryList<PropertyComponent>();
        queryList.reset([component]);
        return queryList;
    }

    private getThingIdEventMap(): Promise<{ [thingId: string]: ThingAlertStatus[] }> {
        let resultMap: { [id: string]: ThingAlertStatus[] } = {};
        let params = new HttpParams();
        params = params.set('locationId', this.locationSchema.locationId);
        [2, 3, 4].forEach(severity => params = params.append('eventSeverity', severity));       // minor anomaly, major anomaly and failure
        this.locationSchemaThingIds.forEach(id => params = params.append('selectedThingId', id));
        return this.userEventService.getRecursivelyAllEvents(null, true, null, params).then(events => {
            events.forEach(event => {
                if (!resultMap[event.thing.id]) {
                    resultMap[event.thing.id] = [event];
                } else {
                    resultMap[event.thing.id].push(event);
                }
            });
            return resultMap;
        });
    }

    private getThingIdMetricValueMap(): Promise<{ [thingId: string]: Value }> {
        if (this.selectedDisplayedValue == LocationSchemaPlanViewerComponent.HEALTH_STATUS) {
            this.visualizedDataDisclaimer = null;
            return Promise.resolve({});
        }
        const metricComponent = this.metricComponentMap[this.selectedDisplayedValue];
        let params = new HttpParams();
        if (!this.range) {
            this.selectedDateRangeName = DateRangeName.LAST_7_DAYS;
            this.updateRange();
        }
        params = params.set('startDate', this.range.start.valueOf());
        params = params.set('endDate', this.range.end.valueOf());
        if (metricComponent.aggregation) {
            params = params.set('aggregation', metricComponent.aggregation);
        }
        this.updateVisualizedDataDisclaimer(metricComponent.aggregation);
        let promises = [];
        let resultMap: { [thingId: string]: any } = {};
        this.locationSchemaThingIds.forEach(thingId => {
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(thingId, metricComponent.name, params).then(value => resultMap[thingId] = value || null).catch(err => {/* ignore */ }));
        });
        return Promise.all(promises).then(() => {
            return resultMap;
        });
    }

    private updateVisualizedDataDisclaimer(aggregation: MetricAggregationType): void {
        const aggregationLabel = this.localizationPipe.transform(this.underscoreRemoverPipe.transform(aggregation || MetricAggregationType.LAST_VALUE));
        let periodLabel;
        if (this.selectedDateRangeName == CUSTOM_RANGE) {
            periodLabel = this.datetimeFormatterPipe.transform(this.range.start.valueOf()) + ' - ' + this.datetimeFormatterPipe.transform(this.range.end.valueOf());
        } else {
            const daterange = this.dateRangeService.getCustomDateRangeByName(this.selectedDateRangeName);
            const dateRangeLabel = daterange?.label ? this.customLabels[daterange.label] : null;
            periodLabel = this.localizationPipe.transform(dateRangeLabel || 'Invalid Period');
        }
        this.visualizedDataDisclaimer = this.localizationPipe.transform("LocationSchema.visualizedDataDisclaimer").replace("${aggregationLabel}", aggregationLabel).replace("${periodLabel}", periodLabel);
    }

    private getThingDataMap(eventsMap: { [thingId: string]: Alert[] }, thingIdMetricValueMap: { [thingId: string]: Value }): { [id: string]: LocationSchemaPlanViewThingData } {
        let thingDataMap: { [id: string]: LocationSchemaPlanViewThingData } = {};
        this.locationSchemaThingIds.forEach(id => {
            const events = eventsMap[id] || [];
            thingDataMap[id] = { thing: this.thingMap[id], components: this.thingIdColumnComponentMap[id], events: events, displayedValue: this.getThingDisplayedValue(events, thingIdMetricValueMap[id]) }
        });
        return thingDataMap;
    }

    private getThingDisplayedValue(events: Alert[], dataValue: Value): LocationSchemaPlanViewDisplayedValue {
        if (this.selectedDisplayedValue == LocationSchemaPlanViewerComponent.HEALTH_STATUS) {
            return this.getHealthStatusValue(events)
        } else {
            const metricComponent = this.metricComponentMap[this.selectedDisplayedValue];
            const metric = this.metrics.find(m => m.name == this.selectedDisplayedValue);
            const unit = !this.filterService.isUnitAware(metricComponent.filter) ? (metricComponent.unit != null ? metricComponent.unit : metric.unit) : null;
            return { value: dataValue?.value != null ? dataValue.value : this.defaultNullValue, class: null, filter: metricComponent?.filter, filterArg: { metric: metric, templateElement: metricComponent?.getTemplateInputMap() }, unit: dataValue?.value != null ? unit : null };
        }
    }

    private getProductionLineDataMap(eventsMap: { [thingId: string]: Alert[] }, workCellDataMap: { [workCellId: string]: LocationSchemaPlanViewWorkCellData }): { [id: string]: LocationSchemaPlanViewProductionLineData } {
        let productionLineDataMap: { [id: string]: LocationSchemaPlanViewProductionLineData } = {};
        this.locationSchema.productionLines?.forEach(pl => {
            let productionLineEvents = [];
            let workCellDataValues: Value[] = [];
            pl.workCells?.forEach(wc => {
                wc.thingIds?.forEach(thingId => {
                    productionLineEvents = productionLineEvents.concat(eventsMap[thingId] || []);
                });
                const wcValue = workCellDataMap[wc.id]?.displayedValue?.value;
                workCellDataValues.push({ value: !isNaN(Number(wcValue)) ? Number(wcValue) : null, timestamp: null, unspecifiedChange: null, privateData: null });
            });
            productionLineDataMap[pl.id] = { displayedValue: this.getDisplayedValue(productionLineEvents, workCellDataValues) }
        });
        return productionLineDataMap;
    }

    private getDisplayedValue(events: Alert[], dataValues: Value[]): LocationSchemaPlanViewDisplayedValue {
        if (this.selectedDisplayedValue == LocationSchemaPlanViewerComponent.HEALTH_STATUS) {
            return this.getHealthStatusValue(events);
        } else {
            const metric = this.metrics.find(m => m.name == this.selectedDisplayedValue);
            let metricComponent = this.metricComponentMap[this.selectedDisplayedValue];
            if (metric && metricComponent && metric.type == MetricType.ALGORITHM && [MetricValueType.DOUBLE, MetricValueType.FLOAT, MetricValueType.INTEGER, MetricValueType.LONG].includes(metric.valueType)) {
                const values = dataValues.filter(value => value?.value != null).map(v => v.value);
                if (!values.length) {
                    return null;
                }
                const sumTotal = values.reduce((total, value) => total + value, 0);
                const unit = !this.filterService.isUnitAware(metricComponent.filter) ? (metricComponent.unit != null ? metricComponent.unit : metric.unit) : null;
                switch (metricComponent.aggregation) {
                    case MetricAggregationType.AVG:
                        const avg = sumTotal / values.length;
                        return { value: avg.toFixed(2), class: null, filter: metricComponent.filter, filterArg: { metric: metric, templateElement: metricComponent.getTemplateInputMap() }, unit: unit };
                    case MetricAggregationType.MIN:
                        return { value: Math.min(...values), class: null, filter: metricComponent.filter, filterArg: { metric: metric, templateElement: metricComponent.getTemplateInputMap() }, unit: unit };
                    case MetricAggregationType.MAX:
                        return { value: Math.max(...values), class: null, filter: metricComponent.filter, filterArg: { metric: metric, templateElement: metricComponent.getTemplateInputMap() }, unit: unit };
                    case MetricAggregationType.SUM:
                        return { value: sumTotal, class: null, filter: metricComponent.filter, filterArg: { metric: metric, templateElement: metricComponent.getTemplateInputMap() }, unit: unit };
                    default:
                        return null;
                }
            }
            return null;
        }
    }

    private getWorkCellDataMap(eventsMap: { [thingId: string]: Alert[] }, thingIdMetricValueMap: { [thingId: string]: Value }): { [id: string]: LocationSchemaPlanViewWorkCellData } {
        let workCellDataMap: { [id: string]: LocationSchemaPlanViewWorkCellData } = {};
        this.locationSchema.productionLines?.forEach(pl => {
            pl.workCells?.forEach(wc => {
                let workCellEvents: Alert[] = [];
                let dataValues: Value[] = [];
                wc.thingIds?.forEach(thingId => {
                    workCellEvents = workCellEvents.concat(eventsMap[thingId] || []);
                    dataValues.push(thingIdMetricValueMap[thingId]);
                });
                workCellDataMap[wc.id] = { displayedValue: this.getWorkCellDisplayedValue(workCellEvents, dataValues) }
            });
        });
        return workCellDataMap;
    }

    private getWorkCellDisplayedValue(events: Alert[], dataValues: Value[]): LocationSchemaPlanViewDisplayedValue {
        return this.getDisplayedValue(events, dataValues);
    }

    private getHealthStatusValue(events: Alert[]): LocationSchemaPlanViewDisplayedValue {
        if (events.length) {
            if (events.some(e => e.eventSeverity == 4 || e.eventSeverity == 3)) {
                return { value: 'CRITICAL', class: 'text-danger', filter: null, filterArg: null, unit: null };
            } else {
                return { value: 'WARNING', class: 'text-warning', filter: null, filterArg: null, unit: null };
            }
        } else {
            return { value: 'NORMAL', class: 'text-normal', filter: null, filterArg: null, unit: null };
        }
    }

    displayedValueTypeChanged(value: string): void {
        this.selectedDisplayedValue = value;
        this.loaded = false;
        this.stopRefreshing();
        this.updatePlanViewData().then(() => this.startRefreshing());
    }

    private startRefreshing(): void {
        this.stopRefreshing();
        this.zone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => {
                this.zone.run(() => {
                    const clonedThingDataMap = _.cloneDeep(this.planViewData.thingDataMap);
                    this.updatePlanViewData().then(() => {
                        const thingIds = this.locationSchemaService.getThingIdsFromLocationSchema(this.locationSchema);
                        if (thingIds.some(thingId =>
                            (clonedThingDataMap[thingId]?.displayedValue?.value != this.planViewData.thingDataMap[thingId]?.displayedValue?.value) ||
                            (clonedThingDataMap[thingId]?.events?.length != this.planViewData.thingDataMap[thingId]?.events?.length)
                        )) {
                            this.loaded = false;
                            setTimeout(() => this.loaded = true, 100);
                        }
                    });
                });
            }, this.refreshIntervalMillis)
        });
    }

    private stopRefreshing(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

}

export class LocationSchemaPlanViewData {
    thingDataMap: { [id: string]: LocationSchemaPlanViewThingData };
    productionLineDataMap: { [id: string]: LocationSchemaPlanViewProductionLineData };
    workCellDataMap: { [id: string]: LocationSchemaPlanViewWorkCellData };
};

export class LocationSchemaPlanViewThingData {
    thing: Thing;
    components: QueryList<PropertyComponent | MetricDetailComponent>;
    events: Alert[];
    displayedValue: LocationSchemaPlanViewDisplayedValue;
}

export class LocationSchemaPlanViewProductionLineData {
    displayedValue: LocationSchemaPlanViewDisplayedValue;
}

export class LocationSchemaPlanViewWorkCellData {
    displayedValue: LocationSchemaPlanViewDisplayedValue;
}